import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'

import { safeStorageSet } from 'src/utils/safeStorage'

type PromoBannerProps = {
  tag?: string
  message: React.ReactNode
  ctaHref?: string
  ctaLabel?: string
  onCloseStorageKey?: string
  className?: string
  containerClassName?: string
  icon?: React.ReactNode
  finePrint?: React.ReactNode
  initialHidden?: boolean
}

const PromoBanner = ({
  tag,
  message,
  ctaHref,
  ctaLabel,
  onCloseStorageKey,
  className,
  containerClassName,
  icon,
  finePrint,
  initialHidden = false
}: PromoBannerProps) => {
  const { t } = useTranslation('common')
  const [hidden, setHidden] = useState(initialHidden)

  if (hidden) return null

  return (
    <div
      className={`w-full overflow-x-hidden bg-cream-300 ${containerClassName ?? ''}`}
    >
      <div className="mx-auto w-full max-w-[1200px] px-4 py-3">
        <div
          className={`relative flex w-full min-w-0 flex-col gap-2 rounded-xl bg-ink-700 px-3 py-2.5 pr-9 text-white-500 ring-1 ring-blue-500/30 md:flex-row md:items-center md:justify-between md:gap-3 md:px-4 md:py-3 md:pr-10 ${className ?? ''}`}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 md:gap-2">
            {icon ?? null}
            {tag ? (
              <span className="inline-flex shrink-0 items-center rounded bg-yellow-500 px-2 py-0.5 text-[10px] font-semibold tracking-wide whitespace-nowrap text-ink-800 uppercase select-none">
                {tag}
              </span>
            ) : null}
            <div className="min-w-0 flex-1 text-xs leading-relaxed md:text-sm">
              {message}
            </div>
          </div>
          {ctaHref && ctaLabel ? (
            <div className="flex shrink-0 items-center md:ml-2">
              <a
                href={ctaHref}
                className="w-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 px-3 py-1.5 text-center text-xs font-semibold text-white-500 shadow-md transition-all hover:scale-105 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white-500 md:w-auto"
              >
                {ctaLabel}
              </a>
            </div>
          ) : null}
          <button
            aria-label={t('A11Y.CLOSE')}
            className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full p-1 text-white-500/80 hover:bg-white-400 hover:text-white-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white-500 md:right-3"
            onClick={() => {
              if (onCloseStorageKey) {
                safeStorageSet('local', onCloseStorageKey, '1')
              }
              setHidden(true)
            }}
          >
            <span className="flex items-center justify-center text-base leading-none">
              ×
            </span>
          </button>
        </div>
        {finePrint ? (
          <div className="mt-1.5 px-3 text-[10px] leading-tight text-ink-200 md:px-4">
            {finePrint}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default React.memo(PromoBanner)
